import h from 'hyperscript';

import { assert } from '@disy/lib/custom-error';
import { debounce } from '@disy/lib/debounce';
import { throttle } from '@disy/lib/throttle';

import './scroll-shadow.css';

const SCROLL_COLUMN_CLASS = 'd-scroll-column';

/**
 * Adds shadows to the given scroll element's header and footer elements.
 * The shadows indicate scrollability in that direction and update on scroll.
 * legacySetupScrollShadow is still being used in a lot of places, for e.g. table.
 * Because of the complexity of the table, it is not easy to migrate to new scroll shadow
 * implementation.
 *
 * @param el - The scroll element
 * @param [header] - The header element
 * @param [footer] - The footer element
 * @return A function to call to update the scroll shadows. It should be called manually after the contents of the scroll container were changed, because it is quite hard to do automatically internally here, but very ease to call from the outside.
 * @deprecated Use `setupScrollShadow` instead
 */
export function legacySetupScrollShadow(el: HTMLElement, header?: HTMLElement, footer?: HTMLElement): () => void {
  if (header) {
    header.classList.add('d-scroll-header');
  }
  if (footer) {
    footer.classList.add('d-scroll-footer');
  }

  function onScroll() {
    if (header) {
      header.classList.toggle('has-shadow', el.scrollTop > 0);
    }
    if (footer) {
      footer.classList.toggle('has-shadow', el.clientHeight + el.scrollTop < el.scrollHeight);
    }
  }
  const onScrollThrottled = throttle(onScroll, 100);

  // Update once on initialization
  onScroll();

  // Update on main element scroll event
  el.addEventListener('scroll', onScrollThrottled, { passive: true });

  // Return the update function, so the initiator can call it manually on its internal events which can cause content resize.
  return onScrollThrottled;
}

/**
 * Adds shadows to the given fixed column element.
 * The shadows indicate scrollability in that direction and update on scroll.
 *
 * @param {HTMLElement} scrollElement - scroll element
 * @param {...HTMLElement} shadowTargets - HTML elements where shadow will be applied
 */
export function setupColumnScrollShadow(scrollElement: HTMLElement, ...shadowTargets: HTMLElement[]) {
  assert(shadowTargets, 'No shadow targets defined.');
  shadowTargets.forEach((target) => target.classList.add(SCROLL_COLUMN_CLASS));

  onScrollLeft();
  scrollElement.addEventListener('scroll', onScrollLeft, { passive: true });

  function onScrollLeft() {
    const showShadow = scrollElement.scrollLeft > 0;
    shadowTargets.forEach((target) => target.classList.toggle('has-shadow', showShadow));
  }
}

export function removeColumnScrollShadow(scrollElement: HTMLElement) {
  const columns = scrollElement.querySelectorAll(`.${SCROLL_COLUMN_CLASS}`);
  [...columns].forEach((column) => {
    column.classList.remove(SCROLL_COLUMN_CLASS);
  });
}

/**
 * Adds shadows to the specified element.
 *
 * @param {HTMLElement} element - Element on which shadows will appear
 * @param {HTMLElement} [scrollElement] - Element where scroll bar is located, if it's not defined,
 *   element is used
 * @return {() => void} - Returns function that can be used to force update.
 */
export function setupScrollShadow(element: HTMLElement, scrollElement = element) {
  // create shadow elements
  const shadowTop = h('.d-scroll-shadow.d-scroll-shadow--top');
  const shadowBottom = h('.d-scroll-shadow.d-scroll-shadow--bottom');

  // remove previous shadows
  scrollElement.parentElement?.querySelector('.d-scroll-shadow--bottom')?.remove();
  scrollElement.parentElement?.querySelector('.d-scroll-shadow--top')?.remove();

  // add new shadows
  scrollElement.after(shadowBottom, shadowTop);

  function updateShadowPositions() {
    // We need to make sure that we clean up event listener for resizing the window when the element
    // is removed from the DOM.
    if (!document.contains(scrollElement)) {
      window.removeEventListener('resize', debouncedUpdateShadowPositions);
    }

    const { width } = element.getBoundingClientRect();
    const { scrollHeight, scrollTop } = scrollElement;

    const { top, left } = jqueryPosition(scrollElement);
    const height = scrollElement.offsetHeight ?? 0;

    Object.assign(shadowTop.style, {
      width: `${width}px`,
      top: `${top}px`,
      left: `${left}px`,
    });

    Object.assign(shadowBottom.style, {
      width: `${width}px`,
      top: `${top + height - 4}px`,
      left: `${left}px`,
    });

    shadowTop.classList.toggle('is-visible', scrollTop > 0);

    shadowBottom.classList.toggle('is-visible', Math.ceil(scrollTop + height) < scrollHeight);
  }

  const debouncedUpdateShadowPositions = debounce(updateShadowPositions);

  scrollElement.addEventListener('scroll', updateShadowPositions);
  const resizeObserver = new ResizeObserver(debouncedUpdateShadowPositions);
  resizeObserver.observe(element);

  debouncedUpdateShadowPositions();

  return updateShadowPositions;
}

/**
 * Adds shadows to the specified element inside specified container. The shadow will not be shown if the element
 * container don't have a scrollbar or if scrollbar will reach bottom position.
 * This function is different from above one. It just adds shadow class to element instead of elements with shadow.
 * It's better in case of floating element because we don't have to sync element movment with added elements.
 * It is needed to determine if element with position: sticky has stopped.
 * Maybe it can be replaced in future with :stuck (not yet implemented).
 *
 *
 * @param element - Element on which shadows will appear
 * @param [scrollElement] - Container where scroll bar is located
 * @return - Returns function that can be used to force update.
 */
export function setupShadowOnStickyElement(
  element: HTMLElement,
  scrollElement?: HTMLElement,
): undefined | typeof checkIfScrolledDown {
  if (!element || !scrollElement) {
    return;
  }
  const debouncedScrollCheck = throttle(checkIfScrolledDown);
  const listener = () => debouncedScrollCheck(element, scrollElement, listener);
  scrollElement.addEventListener('scroll', listener);
  window.addEventListener('resize', listener);
  listener();
  return debouncedScrollCheck;
}

function checkIfScrolledDown(element: HTMLElement, scrollElement: HTMLElement, listener: EventListener) {
  // We need to make sure that we clean up event listener for resizing the window when the element
  // is removed from the DOM.
  if (!scrollElement || !document.contains(scrollElement)) {
    window.removeEventListener('resize', listener);
  }
  const { scrollHeight, scrollTop } = scrollElement;
  const height = scrollElement.offsetHeight ?? 0;

  element.classList.toggle('d-sticky-shadow', Math.ceil(scrollTop + height) < scrollHeight);
}

// Inspired by position() implementation in jquery
function jqueryPosition(el: HTMLElement) {
  const { top, left } = el.getBoundingClientRect();
  const { marginTop, marginLeft, position } = getComputedStyle(el);
  if (position === 'fixed') {
    return {
      top: top - parseInt(marginTop, 10),
      left: left - parseInt(marginLeft, 10),
    };
  } else {
    const parentOffset = { top: 0, left: 0 };
    const docBody = el.ownerDocument.body;
    const docElement = el.ownerDocument.documentElement;
    let offsetParentElement = el.offsetParent || docElement;
    while (
      offsetParentElement &&
      offsetParentElement.parentNode instanceof Element &&
      (offsetParentElement === docBody || offsetParentElement === docElement) &&
      getComputedStyle(offsetParentElement).position === 'static'
      ) {
      offsetParentElement = offsetParentElement.parentNode;
    }
    if (offsetParentElement && offsetParentElement !== el) {
      const { borderTopWidth, borderLeftWidth } = getComputedStyle(offsetParentElement);
      parentOffset.top = offsetParentElement.getBoundingClientRect().top + parseInt(borderTopWidth, 10);
      parentOffset.left = offsetParentElement.getBoundingClientRect().left + parseInt(borderLeftWidth, 10);
    }
    return {
      top: top - parentOffset.top - parseInt(marginTop, 10),
      left: left - parentOffset.left - parseInt(marginLeft, 10),
    };
  }
}
